// Service Worker Registration Utility
// Registra e gerencia o Service Worker para cache estratégico

export interface ServiceWorkerConfig {
  enabled: boolean;
  swPath: string;
  scope: string;
  updateCheckInterval: number;
}

const defaultConfig: ServiceWorkerConfig = {
  enabled: true,
  swPath: '/sw.js',
  scope: '/',
  updateCheckInterval: 60000, // 1 minuto
};

class ServiceWorkerManager {
  private config: ServiceWorkerConfig;
  private registration: ServiceWorkerRegistration | null = null;

  constructor(config: Partial<ServiceWorkerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  async register(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('[SW] Service Worker não suportado neste navegador');
      return false;
    }

    if (!this.config.enabled) {
      console.log('[SW] Service Worker desabilitado');
      return false;
    }

    try {
      this.registration = await navigator.serviceWorker.register(
        this.config.swPath,
        { scope: this.config.scope }
      );

      console.log('[SW] Service Worker registrado com sucesso:', this.registration.scope);

      // Configurar listeners para atualizações
      this.setupUpdateListeners();
      
      // Verificar atualizações periodicamente
      this.startUpdateCheck();

      return true;
    } catch (error) {
      console.error('[SW] Falha ao registrar Service Worker:', error);
      return false;
    }
  }

  private isSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  private setupUpdateListeners(): void {
    if (!this.registration) return;

    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration!.installing;
      if (!newWorker) return;

      console.log('[SW] Nova versão encontrada');

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('[SW] Nova versão disponível');
          this.showUpdateNotification();
        }
      });
    });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] Service Worker atualizado, recarregando página');
      window.location.reload();
    });
  }

  private startUpdateCheck(): void {
    setInterval(() => {
      if (this.registration) {
        this.registration.update();
      }
    }, this.config.updateCheckInterval);
  }

  private showUpdateNotification(): void {
    // Implementar notificação personalizada ou usar toast
    const shouldUpdate = confirm(
      'Uma nova versão do site está disponível. Deseja atualizar agora?'
    );

    if (shouldUpdate) {
      this.skipWaiting();
    }
  }

  private skipWaiting(): void {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  async getVersion(): Promise<string | null> {
    if (!this.registration?.active) return null;

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.version || null);
      };
      
      this.registration!.active!.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    });
  }

  async unregister(): Promise<boolean> {
    if (!this.registration) return false;

    try {
      const result = await this.registration.unregister();
      console.log('[SW] Service Worker desregistrado:', result);
      this.registration = null;
      return result;
    } catch (error) {
      console.error('[SW] Erro ao desregistrar Service Worker:', error);
      return false;
    }
  }

  // Métodos utilitários para cache
  async clearCache(cachePattern?: RegExp): Promise<void> {
    const cacheNames = await caches.keys();
    
    const cachesToDelete = cachePattern
      ? cacheNames.filter(name => cachePattern.test(name))
      : cacheNames;

    await Promise.all(
      cachesToDelete.map(name => {
        console.log('[SW] Limpando cache:', name);
        return caches.delete(name);
      })
    );
  }

  async getCacheInfo(): Promise<{ name: string; size: number }[]> {
    const cacheNames = await caches.keys();
    const cacheInfo = [];

    for (const name of cacheNames) {
      const cache = await caches.open(name);
      const keys = await cache.keys();
      cacheInfo.push({
        name,
        size: keys.length
      });
    }

    return cacheInfo;
  }
}

// Instância global do gerenciador
export const swManager = new ServiceWorkerManager();

// Função de conveniência para registrar o SW
export async function registerServiceWorker(config?: Partial<ServiceWorkerConfig>): Promise<boolean> {
  if (config) {
    const newManager = new ServiceWorkerManager(config);
    return newManager.register();
  }
  
  return swManager.register();
}

// Funções utilitárias para desenvolvimento
export const serviceWorkerUtils = {
  async clearAllCaches(): Promise<void> {
    return swManager.clearCache();
  },

  async getCacheInfo(): Promise<{ name: string; size: number }[]> {
    return swManager.getCacheInfo();
  },

  async getVersion(): Promise<string | null> {
    return swManager.getVersion();
  },

  async unregister(): Promise<boolean> {
    return swManager.unregister();
  }
};

export default ServiceWorkerManager;
